<template>
  <!-- prettier-ignore -->
  <div>
    <label :class="classes">
      <div class="input__title">{{ autoTitle }}</div>
      <div class="input-element">
        <textarea
          ref="input"
          v-autofocus="maybeFocus"
          class="input__element"
          v-bind="$attrs"
          :maxlength="maxLength"
          :name="name"
          :placeholder="placeholder"
          :readonly="readonly"
          :value="value"
          v-on="$listeners"
          @focus="focused = true"
          @blur="emitBlur"
          @[mode]="update"
        />

        <div
          v-if="maxLength"
          class="input__indicator"
          :class="{'input__indicator--error': valueLength >= maxLength}">{{ valueLength }}/{{ maxLength }}</div>
      </div>
      <div class="input__hint">{{ autoHint }}</div>
    </label>
    <slot />
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import FocusDirective from '@/directives/Focus'

import { DEFAULT_DELAY_TIME } from '@/config/constants'

export default {
  name: 'OvpTextarea',

  directives: {
    autofocus: FocusDirective,
  },

  model: {
    prop: 'value',
    event: 'update',
  },

  props: {
    autofocus: {
      type: Boolean,
      default: false,
    },

    delay: {
      type: Number,
      default: DEFAULT_DELAY_TIME * 3,
    },

    hint: {
      type: String,
      default: '',
    },

    isActive: {
      type: Boolean,
      default: false,
    },

    maxLength: {
      type: Number,
      default: 1000,
    },

    name: {
      type: String,
      required: true,
    },

    placeholder: {
      type: String,
      default: '',
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    required: {
      type: Boolean,
      default: false,
    },

    theme: {
      type: String,
      default: 'light',
    },

    title: {
      type: String,
      required: true,
    },

    type: {
      type: String,
      default: 'text',
    },

    validate: {
      type: Object,
      default: () => {},
    },

    value: {
      type: [String, Number],
    },
  },

  data() {
    return {
      focused: false,
    }
  },

  created() {
    this.update = debounce(this.emitUpdate, this.delay)
  },

  computed: {
    active() {
      return this.focused || this.value || (this.value !== undefined && this.value !== null) || this.isActive
    },

    autoHint() {
      const errors = Object.keys(this.validate?.$params ?? {}).filter(error => !this.validate[error])

      if (this.invalid && errors.length > 0) {
        return this.$t(`form.error.${errors[0]}`)
      } else {
        return this.hint
      }
    },

    autoTitle() {
      return `${this.title} ${this.isRequired ? '*' : ''}`
    },

    classes() {
      return {
        input: true,
        'input--focused': this.active,
        'input--invalid': this.invalid,
        'input--readonly': this.readonly,
        [`input--${this.theme}`]: true,
      }
    },

    invalid() {
      return this.validate?.$invalid && this.validate?.$error
    },

    isRequired() {
      return this.required || this.validate?.required !== undefined
    },

    maybeFocus() {
      return this.autofocus && this.$browser.isDesktop
    },

    mode() {
      return this.lazy ? 'change' : 'input'
    },

    valueLength() {
      return this.value?.length ?? 0
    },
  },

  methods: {
    emitBlur($event) {
      this.focused = false
      let value = $event.target.value.trim()

      if (this.value !== value) {
        this.emitUpdate($event)
      }
    },

    emitUpdate($event) {
      this.validate?.$touch()

      let value = $event.target.value.trim()

      if (value === '') value = null
      if (this.isRequired && value === null) return

      this.$emit('update', value)
    },
  },
}
</script>

<style name="mobile" scoped>
.input {
  display: inline-block;
  height: 200px;
  position: relative;
  width: 100%;

  &__title {
    position: absolute;
    font-size: 15px;
    line-height: 16px;
    color: #999999;
    padding: 4px 10px 0 10px;
    top: 15px;
    width: 100%;
    transition: top 0.2s ease-in-out, font-size 0.2s ease-in-out;
    z-index: 1;
    cursor: text;
  }

  &-element {
    position: relative;
    height: 100%;
    padding-top: 20px;
    border-bottom: 1px solid var(--c-black);
    cursor: text;

    transition: border-bottom-color 0.2s ease-in-out;
  }

  &__element {
    background: none;
    border: none;
    outline: none;
    color: var(--c-black);
    font-size: 16px;
    line-height: 20px;
    padding: 0 10px 4px 10px;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }

  &__indicator {
    position: absolute;
    right: 0;
    font-size: 12px;

    &--error {
      color: var(--c-error);
    }
  }

  &__hint {
    font-size: 15px;
    line-height: 20px;
    color: #999999;
    padding: 0 0 0 10px;

    transition: color 0.2s ease-in-out;
  }

  &--focused {
    .input__title {
      font-size: 12px;
      top: 0;
    }

    .input__element {
      opacity: 1;
    }
  }

  &--invalid {
    .input-element {
      border-bottom-color: var(--c-error);
    }

    .input__title,
    .input__hint {
      color: var(--c-error);
    }
  }

  &--readonly {
    .input__title {
      cursor: default;
    }

    .input-element {
      border-bottom-color: transparent;
    }

    .input__element {
      cursor: default;
    }
  }

  &--dark {
    .input-element {
      background-color: var(--c-warm-gray);
    }

    .input__hint {
    }
  }

  &--light {
    background-color: var(--c-white);

    .input-element,
    .input__title {
      background-color: var(--c-white);
    }
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .input {
  }
}
</style>
